import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { FieldValidationValues } from '../types';
import { VALIDATION_PATTERN_OPTIONS } from '../utils/tableDefinitionValidation';
import { EditorField, EditorSectionCard, editorControlStyles } from './EditorSectionCard';

export function FieldValidationSection({
  values,
  onChange,
  sectionRef,
  disabled = false,
}: {
  values: FieldValidationValues;
  onChange: (values: FieldValidationValues) => void;
  sectionRef?: React.RefObject<HTMLDivElement>;
  disabled?: boolean;
}) {
  const selectedPattern =
    VALIDATION_PATTERN_OPTIONS.find((option) => option.value === values.validationPattern) ||
    VALIDATION_PATTERN_OPTIONS[0];

  return (
    <EditorSectionCard
      sectionRef={sectionRef}
      id='table-definition-validation'
      icon={<VerifiedUserOutlinedIcon fontSize='small' />}
      title='Input validation'
      description='Control the values users can enter for this field.'
      badge='Optional'
    >
      <Stack gap={2.25} maxWidth={760}>
        <EditorField label='Validation pattern'>
          <Autocomplete
            value={selectedPattern}
            options={VALIDATION_PATTERN_OPTIONS}
            disabled={disabled}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            clearIcon={null}
            onChange={(_, option) => onChange({ ...values, validationPattern: option?.value ?? null })}
            sx={editorControlStyles}
            renderInput={(params) => <TextField {...params} size='small' />}
          />
        </EditorField>

        {values.validationPattern === 'VALIDATION_PATTERN_CUSTOM' && (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 2,
              }}
            >
              <EditorField label='Minimum characters'>
                <TextField
                  size='small'
                  type='number'
                  disabled={disabled}
                  inputProps={{ min: 0 }}
                  value={values.minCharacter ?? ''}
                  sx={editorControlStyles}
                  onChange={(event) =>
                    onChange({
                      ...values,
                      minCharacter: event.target.value === '' ? null : Number(event.target.value),
                    })
                  }
                />
              </EditorField>
              <EditorField label='Maximum characters'>
                <TextField
                  size='small'
                  type='number'
                  disabled={disabled}
                  inputProps={{ min: 1, max: 255 }}
                  value={values.maxCharacter ?? ''}
                  sx={editorControlStyles}
                  onChange={(event) =>
                    onChange({
                      ...values,
                      maxCharacter: event.target.value === '' ? null : Number(event.target.value),
                    })
                  }
                />
              </EditorField>
            </Box>
            <Box
              sx={{
                border: '1px solid #E7ECF4',
                borderRadius: 2,
                bgcolor: '#FAFBFD',
                px: 2,
                py: 1,
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={disabled}
                    checked={values.noSpace}
                    sx={{ color: '#98A2AE', '&.Mui-checked': { color: '#42BB93' } }}
                    onChange={(event) => onChange({ ...values, noSpace: event.target.checked })}
                  />
                }
                label='No Spaces Allowed'
              />
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={disabled}
                    checked={values.noSpecialCharacter}
                    sx={{ color: '#98A2AE', '&.Mui-checked': { color: '#42BB93' } }}
                    onChange={(event) => onChange({ ...values, noSpecialCharacter: event.target.checked })}
                  />
                }
                label='No Special Characters Allowed'
              />
            </Box>
          </>
        )}
      </Stack>
    </EditorSectionCard>
  );
}
